import { RecurringDocumentStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { queriesRecurringInvoices } from "@query";
import { WSQueries } from "../../query/WSQuery";
import { getLocalStartDayByUTC } from "../../utils/dates";
import { InvoicesForm } from "../components/InvoicesForm/InvoicesForm";
import { useDefaultInvoiceSubject } from "../utils";
import {
  attachmentsToFormData,
  convertToLineItemFormDataFromRecurring,
  creditFeeToFormDataFromV3,
  frequencyToFormDataFromV3,
  lateFeeToFormDataFromV3,
  paymentMethodsToFormDataFromV3,
  recurringSplitsToFormData
} from "../utils/invoicesFormUtils";

type Props = RouteComponentProps<{ invoiceTemplateId: string }>;

export const InvoicesInvoiceTemplateEdit: React.FC<Props> = props => {
  const recurringInvoiceId = props.match.params.invoiceTemplateId;
  const queryRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery({
      recurringInvoiceId
    });

  const defaultSubject = useDefaultInvoiceSubject();

  return (
    <WSQueries queries={{ queryRecurringInvoice }}>
      {({ queryRecurringInvoice: { data: recurringInvoice } }) => {
        const invoiceTemplate = recurringInvoice.invoiceTemplate;
        const scheduledDate =
          recurringInvoice.schedule?.scheduleDates?.[0]?.date ??
          recurringInvoice.nextOccurrenceDate;
        const nextOccurrenceDate = scheduledDate
          ? getLocalStartDayByUTC(scheduledDate)
          : new Date();

        const dueDate = new Date(nextOccurrenceDate);
        dueDate.setDate(
          dueDate.getDate() + (invoiceTemplate?.paymentTermsDays ?? 0)
        );

        return (
          <InvoicesForm
            context={{ recurringInvoice }}
            defaultValues={{
              subject: String(
                recurringInvoice.metadata?.subject || defaultSubject
              ),
              payer: {
                payerId: recurringInvoice.payerId || null,
                payerEngagementId: recurringInvoice.payerEngagementId || null,
                emailsCC: []
              },
              purchaseOrderNumber:
                recurringInvoice.metadata?.purchaseOrderNumber || "",
              lineItems: (invoiceTemplate?.lineItems || []).map(
                convertToLineItemFormDataFromRecurring
              ),
              other: {
                projectName: String(
                  recurringInvoice.metadata?.projectName || ""
                ),
                notes: invoiceTemplate?.notes || "",
                attachments: attachmentsToFormData(
                  invoiceTemplate?.customAttachmentIds
                )
              },
              due: "custom",
              customDueDate: dueDate,
              includesLateFee:
                !!invoiceTemplate?.lateFeeHandling &&
                !!invoiceTemplate.lateFeeHandling.frequency,
              lateFee: lateFeeToFormDataFromV3(
                invoiceTemplate?.lateFeeHandling
              ),
              send: {
                type: "date",
                date: nextOccurrenceDate
              },
              recurring: !!recurringInvoice.schedule?.frequency,
              frequency: frequencyToFormDataFromV3(recurringInvoice.schedule),
              splits: recurringSplitsToFormData(invoiceTemplate?.splits),
              paymentMethods: paymentMethodsToFormDataFromV3(
                invoiceTemplate?.acceptedPaymentMethods
              ),
              advanced: {
                sendEmails:
                  !!invoiceTemplate?.notificationPreferences?.shouldSendInvoice,
                sendReminders:
                  !!invoiceTemplate?.notificationPreferences
                    ?.shouldSendReminders,
                creditFeeHandling: creditFeeToFormDataFromV3(
                  invoiceTemplate?.creditFeeHandling
                ),
                date:
                  (recurringInvoice.metadata?.invoiceDate
                    ? new Date(recurringInvoice.metadata.invoiceDate)
                    : undefined) ||
                  (recurringInvoice.status === RecurringDocumentStatus.DRAFT
                    ? new Date()
                    : recurringInvoice.events?.createdAt) ||
                  new Date()
              }
            }}
          />
        );
      }}
    </WSQueries>
  );
};
